import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import HeaderSection from "./HeaderSection";
import QuestionSection from "./QuestionSection";
import { fetchConstructData, fetchGameTabStatusForReporting, setActiveTab } from '../../../Actions/GamesReportsStatusActions/GamesReportsStatusActions';
import SearchUserSection from "./searchUserSection";
import ImagineThisSection from "./ImagineThisSection";
import ConstructRenderer from "./ConstructRenderer";
import "../studentPerformance.css";
import chartDescriptions, { chartConstructDescriptions } from './chartDescriptions';
import NewQuestionSection from "./newQuestionSection";


export const getColorClass = (tab) => {
  switch (tab) {
    case "Slide":
      return "blue";
    case "Stack":
      return "purple";
    case "Match":
      return "indigo";
    default:
      return "blue";
  }
};

const StudentPerformanceDashboard = ({ isHeaderManu, isReporting }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { activeTab, userId, pageOverview, error, tabs, contextData } = useSelector(
    (state) => state.studentPerformenceConstruct
  );
  let { overview, questions, scenarios } = pageOverview;
  const [showReport, setShowReport] = useState(false);

  useEffect(async () => {
    let user_id = isReporting ? { user_id: userId } : {}
    // Skip tab loading for Divergent Thinking as it doesn't use game tabs
    if (location.state.activeConstruct === 'DivergentThinking') {


      return;
    }
    if (!isReporting || (userId && showReport)) {
      dispatch(setActiveTab(''))
      dispatch(fetchGameTabStatusForReporting(
        {
          category: location.state.activeConstruct,
          ...user_id
        }
      ))
    }
  }, [location?.state?.activeConstruct, userId, showReport])


  useEffect(() => {
    if (location?.state?.activeConstruct) {
      // For Divergent Thinking, only set overview data, no game-specific data needed
      if (location.state.activeConstruct === 'DivergentThinking') {
        dispatch({
          type: "SET_OVERVIEW_DATA",
          payload: {
            category: location.state.activeConstruct,
            gameName: 'slide', // Default game name for overview data
            user_id: userId,
          },
        });


        return;
      }

      // For other constructs, require activeTab
      if (activeTab) {
        dispatch({
          type: "SET_OVERVIEW_DATA",
          payload: {
            category: location.state.activeConstruct,
            gameName: activeTab,
            user_id: userId,
          },
        });

        if (!isReporting || userId) {
          let user_id = isReporting ? { user_id: userId } : {}
          dispatch(
            fetchConstructData({
              game: activeTab,
              category: location.state.activeConstruct,
              ...user_id
            })
          );
        }
      }
    }
  }, [location?.state?.activeConstruct, dispatch, activeTab, isReporting]);

  const handleSetUser = useCallback((event) => {
    let id = event.target.value
    dispatch({
      type: "SET_USER_ID_FOR_REPORTIG",
      payload: id
    })
    setShowReport(false)
  }, [])

  const handleShowReport = useCallback(() => {
    if (activeTab) {
      dispatch(fetchConstructData(({ game: activeTab, category: location.state.activeConstruct, user_id: userId })));
    }
    setShowReport(true)
  }, [userId, activeTab, location.state.activeConstruct])

  const filteredTabs = useMemo(() => tabs.filter((tab) => !tab?.disabled), [tabs]);
  const currentConstruct = useMemo(() => {
    if (overview && overview?.title) {
      return overview.title.split(':')?.[0]?.trim()?.toLowerCase();
    }
    // Fallback to location state if overview is not available
    return location?.state?.activeConstruct?.toLowerCase() || '';
  }, [overview, location?.state?.activeConstruct]);
  const descriptions = useMemo(() => chartDescriptions?.[currentConstruct] || {}, [currentConstruct]);
  const constructDescriptions = useMemo(() => chartConstructDescriptions?.[currentConstruct] || {}, [currentConstruct])

  // Provide mock contextData for Divergent Thinking since it doesn't use real API data
  const effectiveContextData = useMemo(() => {
    if (location?.state?.activeConstruct === 'DivergentThinking') {
      return {
        construct_label: [
          {
            construct_id: 'divergent_thinking',
            label: 'Divergent Thinking',
            behavior_graph_key: 'DivergentThinking_Creative',
            percentage: '75' // Mock percentile for demonstration
          }
        ],
        behaviors: {}
      };
    }
    return contextData;
  }, [contextData, location?.state?.activeConstruct]);



  return (
    <div className="min-h-screen bg-background">
      <main className="talwind-dashboard max-w-xs mx-auto pt-24 pb-10 pr-3 flex flex-col gap-6 w-full h-full transition-all duration-300 sm:max-w-[420px] md:max-w-[calc(100vw-80px)] dark:bg-gray-900 dark:text-gray-100">
        <div className={`${isHeaderManu ? "md:w-full lg:w-full xl:w-full 2xl:w-full 3xl:w-full md:pl-16" : ""} flex flex-col gap-6 transition-all duration-300 md:w-[72%] lg:w-[79%] xl:w-[84.5%] 2xl:w-[86.5%] 3xl:w-[90%] md:ml-auto`}>
          {isReporting &&
            <SearchUserSection
              handleSetUser={handleSetUser}
              handleShowReport={handleShowReport}
            />
          }
          {
            (!userId && isReporting) || (!showReport && isReporting) ?
              (
                <div className='flex items-center justify-center h-full'>
                  <p className="text-center text-gray-500">Please select a user</p>
                </div>
              )
              : (filteredTabs.length === 0 && location?.state?.activeConstruct !== 'DivergentThinking') ? (
                <div className='flex items-center justify-center h-full'>
                  <p className="text-center text-gray-500">Reports are not available for this construct.</p>
                </div>
              )
                : error === 'Invalid user id' ? (
                  <div className='flex items-center justify-center h-full'>
                    <p className="text-center text-gray-500">Please search by valid user id</p>
                  </div>
                )
                  : <>
                    <HeaderSection title={overview?.title} />
                    {/* <Tabs tabs={tabs} activeTab={activeTab} onClick={handleTabChange} /> */}

                    {/* Render appropriate construct sections */}
                    <ConstructRenderer
                      currentConstruct={currentConstruct}
                      contextData={effectiveContextData}
                      descriptions={descriptions}
                      constructDescriptions={constructDescriptions}
                      isLoading={false}

                    />
                    {/* <QuestionSection questions={questions} /> */}
                    <NewQuestionSection questions={questions} />
                    <ImagineThisSection scenarios={scenarios} activeTab={activeTab} currentConstruct={currentConstruct} />

                  </>
          }

        </div>
      </main>
    </div>
  );
};

export default StudentPerformanceDashboard;
